// The ServiceTitan move's mappings (migration.mappings): what each ServiceTitan reference value
// becomes here (migration.mapValue: a department, job type, tax zone, membership plan or payment kind,
// or "create one"), and the owner's approval (migration.approveMappings). Every value arrives with our
// recommendation pre-selected and why (#82: new as in ServiceTitan, or an existing value of its name),
// so the owner scans, overrides where they disagree (one change of the select) and approves a year's,
// a kind's or one row's at once, each count shown first. Changing a value makes it wait for approval
// again; the rest stay approved.

import { useState } from 'react';
import { client } from '../api.ts';
import { Empty, Loading, Panel, errorText, useQuery } from '../books/ui.tsx';
import { useDesign } from '../blueprint/design.ts';
import { Section } from '@flowology/ui-web';
import { SaveButton } from '../shared/SaveButton.tsx';
import {
  type PartProps,
  approveText,
  choiceOf,
  createAllText,
  GROUP_LABEL,
  groupState,
  holds,
  mapInput,
  openByYear,
  OPERATE_PERMS,
  OWNER_PERMS,
  PAYMENT_METHODS,
  toApprove,
  toCreateAll,
  unmappedIn,
  usedInText,
  type Group,
  type Mapping,
} from './model.ts';

interface Named {
  id: string;
  name: string;
}

/** The records a group's values map to, each group from its own list. */
function useTargets() {
  const units = useQuery<{ businessUnits: Named[] }>('businessUnits.list', {});
  const types = useQuery<{ jobTypes: Named[] }>('jobTypes.list', {});
  const zones = useQuery<{ zones: (Named & { active?: boolean })[] }>('tax.listZones', {});
  const plans = useQuery<{ plans: Named[] }>('membershipTypes.list', {});
  const stock = useQuery<{ locations: Named[] }>('stockLocations.list', {});
  return (group: string): Named[] => {
    if (group === 'business_unit') return units.data?.businessUnits ?? [];
    if (group === 'job_type') return types.data?.jobTypes ?? [];
    // a zone retired in ServiceTitan isn't a target for new values (#83)
    if (group === 'tax_zone') return (zones.data?.zones ?? []).filter((z) => z.active !== false);
    if (group === 'membership_type') return plans.data?.plans ?? [];
    if (group === 'stock_location') return stock.data?.locations ?? [];
    return PAYMENT_METHODS.map((p) => ({ id: p.value, name: p.label }));
  };
}

export function Mappings({ held, toast }: PartProps) {
  const q = useQuery<{ mappings: Mapping[]; groups: Group[] }>('migration.mappings', {});
  const targets = useTargets();
  // Groups with values to map open first; a group stays open while it's worked on, until closed.
  const [open, setOpen] = useState<Set<string> | null>(null);
  // Blueprint: the groups are parts of one section, then what waits by year: two sections, not seven
  const bp = useDesign() === 'blueprint';
  if (q.data && open === null)
    setOpen(new Set(q.data.groups.filter((g) => g.unmapped > 0).map((g) => g.group)));
  if (!q.data) return <Loading error={q.error} what="the mappings" retry={() => void q.reload()} />;
  if (!q.data.groups.length)
    return (
      <Panel title="Mappings">
        <Empty>Nothing to map yet: the values appear once the extraction has read them.</Empty>
      </Panel>
    );
  const onChanged = async (said: string) => {
    toast(said);
    await q.reload();
  };
  const groups = (
    <>
      {q.data.groups.map((g) => (
        <GroupPanel
          key={g.group}
          group={g}
          mappings={q.data!.mappings.filter((m) => m.group === g.group)}
          targets={targets(g.group)}
          held={held}
          open={!!open?.has(g.group)}
          onToggle={() =>
            setOpen((o) => {
              const n = new Set(o);
              if (n.has(g.group)) n.delete(g.group);
              else n.add(g.group);
              return n;
            })
          }
          onChanged={onChanged}
        />
      ))}
    </>
  );
  if (bp)
    return (
      <>
        <Section title="Mapping groups" count={q.data.groups.length}>
          {groups}
        </Section>
        <ByYear mappings={q.data.mappings} held={held} onChanged={onChanged} />
      </>
    );
  return (
    <>
      <ByYear mappings={q.data.mappings} held={held} onChanged={onChanged} />
      {groups}
    </>
  );
}

/**
 * Open values by year and kind (#82): what each year's records use that isn't approved yet, and the
 * owner's "Approve all for 2019", its count shown and asked first.
 */
function ByYear({
  mappings,
  held,
  onChanged,
}: {
  mappings: Mapping[];
  held: readonly string[];
  onChanged: (said: string) => Promise<void>;
}) {
  const rows = openByYear(mappings);
  const kinds = Object.keys(GROUP_LABEL).filter((k) => rows.some((r) => r.byGroup[k]));
  const mayApprove = holds(held, OWNER_PERMS);
  if (!rows.length) return null;
  return (
    <Panel title="Waiting for approval, by year">
      {rows.every((r) => r.total === 0) ? (
        <p className="muted small">Every value every year uses is approved.</p>
      ) : (
        <div className="table-wrap">
          <table className="grid-table" aria-label="Open values by year and kind">
            <thead>
              <tr>
                <th>Year</th>
                {kinds.map((k) => (
                  <th key={k} className="num">
                    {GROUP_LABEL[k]}
                  </th>
                ))}
                <th className="num">All</th>
                {mayApprove && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.year}>
                  <td>{r.year}</td>
                  {kinds.map((k) => (
                    <td key={k} className="num">
                      {r.byGroup[k] ?? 0}
                    </td>
                  ))}
                  <td className="num">{r.total}</td>
                  {mayApprove && (
                    <td>
                      {r.total > 0 && (
                        <BulkApprove
                          scope={{ year: r.year }}
                          mappings={mappings}
                          label={`Approve all for ${r.year}`}
                          onChanged={onChanged}
                        />
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/** A bulk approval: its count on the button, asked first in words, then approved as they stand. */
function BulkApprove({
  scope,
  mappings,
  label,
  onChanged,
}: {
  scope: { year?: number; group?: string };
  mappings: Mapping[];
  label: string;
  onChanged: (said: string) => Promise<void>;
}) {
  const these = toApprove(mappings, scope);
  const unmapped = unmappedIn(these);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!these.length) return null;
  if (unmapped.length)
    return (
      <span className="muted small">
        {unmapped.length} to map first:{' '}
        {unmapped
          .slice(0, 3)
          .map((m) => m.stName)
          .join(', ')}
        {unmapped.length > 3 ? ', …' : ''}
      </span>
    );
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ approvedValues: number }>('migration.approveMappings', scope);
      setAsking(false);
      await onChanged(
        `${these.length.toLocaleString('en-CA')} approved${scope.year ? ` for ${scope.year}` : ''}.`,
      );
      void r;
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return asking ? (
    <div className="notice" role="group" aria-label={label}>
      <p>{approveText(scope, these.length)}</p>
      <div className="row-actions">
        <SaveButton busy={busy} busyLabel="Approving…" error={error} onClick={() => void go()}>
          Approve {these.length.toLocaleString('en-CA')}
        </SaveButton>
        <button type="button" className="btn quiet" disabled={busy} onClick={() => setAsking(false)}>
          Cancel
        </button>
      </div>
    </div>
  ) : (
    <button type="button" className="btn" onClick={() => setAsking(true)}>
      {label} ({these.length.toLocaleString('en-CA')})
    </button>
  );
}

function GroupPanel({
  group: g,
  mappings,
  targets,
  held,
  open,
  onToggle,
  onChanged,
}: {
  group: Group;
  mappings: Mapping[];
  targets: Named[];
  held: readonly string[];
  open: boolean;
  onToggle: () => void;
  onChanged: (said: string) => Promise<void>;
}) {
  const label = GROUP_LABEL[g.group] ?? g.group;
  const bp = useDesign() === 'blueprint';
  const mayMap = holds(held, OPERATE_PERMS);
  const mayApprove = holds(held, OWNER_PERMS);
  // "Create all as new" (M9): every value still open set to create one, each through mapValue so each
  // is audited; asked first, in words
  const toCreate = toCreateAll(g.group, mappings);
  const [asking, setAsking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const createAll = async () => {
    setCreating(true);
    setCreateError(null);
    let done = 0;
    try {
      for (const m of toCreate) {
        await client.call('migration.mapValue', mapInput(m, 'create'));
        done++;
      }
      setAsking(false);
      await onChanged(`${label}: ${done} to be created at the load.`);
    } catch (e) {
      setCreateError(`${done} of ${toCreate.length} set. ${errorText(e)}`);
      await onChanged(`${label}: ${done} to be created at the load.`);
    } finally {
      setCreating(false);
    }
  };
  const said = groupState(g);
  const state = <span className={`chip say${said.tone ? ` ${said.tone}` : ''}`}>{said.text}</span>;
  const body = (
    <>
      <p className="muted small">
        {g.values} {g.values === 1 ? 'value' : 'values'}
        {g.approved && g.approvedBy && !/^\w+:/.test(g.approvedBy) ? ` · approved by ${g.approvedBy}` : ''}.
        Changing one withdraws the group’s approval.
      </p>
      <button type="button" className="btn quiet" aria-expanded={open} onClick={onToggle}>
        {open ? 'Hide the values' : 'See the values'}
      </button>
      {open && (
        <ul className="rows mig-maps">
          {mappings.map((m) => (
            <MapRow
              key={m.stId}
              m={m}
              targets={targets}
              mayMap={mayMap}
              mayApprove={mayApprove}
              onMapped={(said) => onChanged(said)}
            />
          ))}
        </ul>
      )}
      {mayMap && !g.approved && toCreate.length > 0 && !asking && (
        <div className="row-actions">
          <button type="button" className="btn" onClick={() => setAsking(true)}>
            Create all as new
          </button>
        </div>
      )}
      {asking && (
        <div className="notice" role="group" aria-label="Create all as new">
          <p>{createAllText(g.group, toCreate.length)}</p>
          <div className="row-actions">
            <SaveButton
              busy={creating}
              busyLabel="Creating…"
              error={createError}
              onClick={() => void createAll()}
            >
              Create {toCreate.length}
            </SaveButton>
            <button type="button" className="btn quiet" disabled={creating} onClick={() => setAsking(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {mayApprove && !g.approved && (
        <div className="row-actions">
          <BulkApprove
            scope={{ group: g.group }}
            mappings={mappings}
            label={`Approve all ${label.toLowerCase()}`}
            onChanged={onChanged}
          />
        </div>
      )}
      {!mayApprove && !g.approved && <p className="muted small">The owner approves each group.</p>}
    </>
  );
  if (bp)
    return (
      <div className="bp-part mig-group" role="group" aria-label={label}>
        <div className="panel-head">
          <h3>{label}</h3>
          {state}
        </div>
        {body}
      </div>
    );
  return (
    <Panel title={label} aside={state}>
      {body}
    </Panel>
  );
}

function MapRow({
  m,
  targets,
  mayMap,
  mayApprove,
  onMapped,
}: {
  m: Mapping;
  targets: Named[];
  mayMap: boolean;
  mayApprove: boolean;
  onMapped: (said: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const choice = choiceOf(m);
  const name = (id: string) => targets.find((t) => t.id === id)?.name ?? id;
  const set = async (next: string) => {
    if (next === choice) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('migration.mapValue', mapInput(m, next));
      await onMapped(
        next === 'create'
          ? `${m.stName}: the load creates it.`
          : next
            ? `${m.stName} becomes ${name(next)}.`
            : `${m.stName} isn’t mapped now.`,
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('migration.approveMappings', { group: m.group, stIds: [m.stId] });
      await onMapped(`${m.stName}: approved.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="mig-map">
      <div>
        <b>{m.stName}</b>
        <span className="muted small block">
          {m.uses.toLocaleString('en-CA')} {m.uses === 1 ? 'record' : 'records'} · used in{' '}
          {usedInText(m.years)}
          {m.why ? ` · ${m.why}` : ''}
          {m.note ? ` · ${m.note}` : ''}
        </span>
      </div>
      {mayMap ? (
        <label className="mig-map-pick">
          <span className="sr-only">{m.stName} becomes</span>
          <select
            className="field"
            value={choice}
            disabled={busy}
            aria-busy={busy || undefined}
            onChange={(e) => void set(e.target.value)}
          >
            <option value="">Not mapped</option>
            {targets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
            <option value="create">Create one at the load</option>
          </select>
          {error && (
            <span className="error small" role="alert">
              {error}
            </span>
          )}
        </label>
      ) : (
        <span>{m.createNew ? 'Created at the load' : m.target ? name(m.target) : 'Not mapped'}</span>
      )}
      {m.approved ? (
        <span className="chip say ok">Approved</span>
      ) : (
        mayApprove &&
        choice !== '' && (
          <button
            type="button"
            className="btn quiet sm"
            disabled={busy}
            aria-label={`Approve ${m.stName}`}
            onClick={() => void approve()}
          >
            Approve
          </button>
        )
      )}
    </li>
  );
}
